import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, Moon } from 'lucide-react';
import { useCallback } from 'react';

import { PageHeader } from '@/components/layout/PageHeader';
import { RequireCapability } from '@/components/layout/RequireCapability';
import { LiveDataIndicator } from '@/components/primitives/LiveDataIndicator';
import { ErrorState } from '@/components/states/ErrorState';
import { SkeletonTable } from '@/components/states/Skeleton';
import { StateBlock } from '@/components/states/StateBlock';
import { QueuePanel } from '@/features/dataops/QueuePanel';
import { SchedulerTable } from '@/features/dataops/SchedulerTable';
import { StuckWeeksPanel } from '@/features/dataops/StuckWeeksPanel';
import { UpstreamPanel } from '@/features/dataops/UpstreamPanel';
import {
  clockOffsetMs,
  formatSpan,
  isOverdueNow,
  overdueForMs,
  useSchedulers,
  useSchedulerWatch,
  useSecondTick,
} from '@/features/dataops/schedulers';
import { activeEnvironment } from '@/lib/environment';
import { environmentStyle } from '@/lib/environment-style';

export const Route = createFileRoute('/data-ops')({
  component: DataOpsScreen,
});

const STATUS_STYLE = {
  healthy: { text: 'var(--ok)', label: 'all schedulers running' },
  degraded: { text: 'var(--warn)', label: 'something is failing' },
  broken: { text: 'var(--err-soft)', label: 'a scheduler is not running' },
} as const;

/**
 * Data Ops.
 *
 * The question this page has to answer on a Sunday, without anyone to ask: is every worker
 * running, when did each last run, when does each run next, is any of them late, is any league's
 * week frozen — and can I unfreeze it from here.
 *
 * Ordered worst-first and deliberately boring. The operator opened it because something is wrong,
 * so the broken thing is at the top and the counts that used to be the whole page are at the
 * bottom, where they belong: queue depth cannot see a dead worker at all.
 */
function DataOpsScreen() {
  const { data, isPending, isFetching, isError, error, refetch, dataUpdatedAt } =
    useSchedulers();

  // The browser's clock, shifted onto the API's. Every countdown on this page is measured against
  // this — a laptop running eight seconds fast must not be able to invent an overdue worker.
  const offsetMs = clockOffsetMs(data?.serverNow, dataUpdatedAt || undefined);
  const tick = useSecondTick(Boolean(data));
  const nowMs = tick + offsetMs;

  const doRefetch = useCallback(() => void refetch(), [refetch]);
  // "Did it actually run?" — the check that turns the countdown from decoration into a detector.
  useSchedulerWatch(data?.schedulers, offsetMs, doRefetch);

  const rows = data?.schedulers ?? [];
  const overdue = rows.filter((row) => isOverdueNow(row, nowMs));
  const dark = rows.filter((row) => row.state === 'dark');
  const unreadable = rows.filter((row) => row.unreadable);
  const noWorker = rows.filter((row) => row.enabled && row.noWorker);

  const status = STATUS_STYLE[data?.status ?? 'healthy'];
  const envStyle = environmentStyle(data?.meshEnv);
  // The API reports which environment it administers. If that disagrees with the badge this build
  // is wearing, one of them is lying and an operator is about to act on the wrong cluster.
  const envMismatch = data !== undefined && data.meshEnv !== activeEnvironment;

  return (
    <RequireCapability capability="dataops.view">
      <div className="flex flex-col gap-3.5">
        <PageHeader
          eyebrow="Operations"
          title="Data Ops"
          meta={
            data ? (
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span style={{ color: status.text }}>{status.label}</span>
                <span className="text-tx3">·</span>
                <span className="text-tx3">
                  {rows.filter((row) => row.enabled).length} of {rows.length} schedulers
                  registered
                </span>
                <span className="text-tx3">·</span>
                {/* Never ambiguous. Staging and production are separate Redis instances, and this
                    is the server's own answer rather than the badge this build happens to wear. */}
                <span
                  className="font-head rounded px-1.5 py-px text-[10.5px] font-bold uppercase"
                  style={{
                    letterSpacing: '0.14em',
                    background: envStyle.bg,
                    border: `1px solid ${envStyle.border}`,
                    color: envStyle.text,
                  }}
                >
                  {envStyle.label}
                </span>
              </span>
            ) : undefined
          }
          actions={
            <LiveDataIndicator
              updatedAt={dataUpdatedAt || undefined}
              isFetching={isFetching}
              isLive
              staleAfterMs={60_000}
              onRefresh={doRefetch}
            />
          }
        />

        {envMismatch && (
          <StateBlock
            icon={AlertTriangle}
            tone="danger"
            title={`This API administers ${data.meshEnv}, not ${activeEnvironment}`}
            description="The console badge and the API disagree about which environment you are on. Every action on this page acts on the API's environment. Do not run anything here until this is resolved."
          />
        )}

        {isError ? (
          <ErrorState error={error} subject="scheduler health" onRetry={doRefetch} />
        ) : isPending ? (
          <SkeletonTable columns={6} />
        ) : (
          <>
            {/* Worst first, and a frozen league outranks everything else on the page. */}
            <StuckWeeksPanel />

            {unreadable.length > 0 && (
              <StateBlock
                icon={AlertTriangle}
                tone="danger"
                title={`${unreadable.length} queue${unreadable.length === 1 ? '' : 's'} could not be read`}
                description="Redis did not answer for these, so worker health is unknown. They are reported as unreadable rather than omitted — a queue silently dropped from this list would leave the page green while scoring is dead."
                detail={unreadable.map((row) => row.queue).join(', ')}
              />
            )}

            {overdue.length > 0 && (
              <StateBlock
                icon={AlertTriangle}
                tone="danger"
                title={`${overdue.length} scheduler${overdue.length === 1 ? ' is' : 's are'} overdue`}
                description="These are registered and should have run. Nothing did. If one of them is tick-runtime-lifecycle during a live slate, that is the whole live week stopped."
                detail={overdue
                  .map((row) => {
                    const late = overdueForMs(row, nowMs);
                    return `${row.jobName}: ${late === null ? 'late' : `${formatSpan(late)} late`}`;
                  })
                  .join(' · ')}
              />
            )}

            {dark.length > 0 && (
              <StateBlock
                icon={Moon}
                tone="danger"
                title={`${dark.length} launch-critical scheduler${dark.length === 1 ? ' is' : 's are'} switched off`}
                description="Not registered at all, and not off by design. tick-notifications is the one to watch here: event-driven notifications keep delivering while it is dark, so the product looks fine and only the timed half — games starting, waiver reminders, participation deadlines — never fires."
                detail={dark.map((row) => `${row.jobName} (${row.flagKey})`).join(' · ')}
              />
            )}

            {noWorker.length > 0 && overdue.length === 0 && (
              <StateBlock
                icon={AlertTriangle}
                tone="warning"
                title={`${noWorker.length} queue${noWorker.length === 1 ? ' has' : 's have'} no worker consuming them`}
                description="A scheduler is registered but nothing is listening. This never shows up as queue depth: with no consumer, nothing promotes the delayed job either, so the queue reads as perfectly idle."
                detail={noWorker.map((row) => row.queue).join(', ')}
              />
            )}

            <SchedulerTable rows={rows} nowMs={nowMs} flags={data.flags.schedulers} />

            <UpstreamPanel />

            <div className="mt-1 flex flex-col gap-2">
              <h2
                className="font-head text-tx3 text-[11px] font-semibold uppercase"
                style={{ letterSpacing: '0.14em' }}
              >
                Queue depth
              </h2>
              <QueuePanel queues={data.queues} />
            </div>

            <p className="text-tx3 max-w-[86ch] text-[11.5px]">
              Queue and scheduler names are ZazzBackend's verbatim and are asserted against its
              source in CI, in both directions — a renamed job would otherwise show here as a
              scheduler that is permanently missing. &ldquo;Last run&rdquo; comes from each
              worker&rsquo;s own heartbeat, not from BullMQ&rsquo;s completed list: scheduled
              jobs are deleted on success, so that list is empty for exactly the jobs this page
              is about.
            </p>
          </>
        )}
      </div>
    </RequireCapability>
  );
}
